import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import moment from "moment";
import { useTheme } from "@mui/material/styles";
import { Box } from "@mui/material";


const TimelineChart: React.FC<{ cyberGraphData: any[], isCloud: boolean, timeRange: string }> = ({ cyberGraphData, isCloud, timeRange }) => {
  const theme = useTheme();
  const timelineChartRef = useRef<HTMLDivElement>(null);
  // const [timeRange, setTimeRange] = useState<string>("7");

  useEffect(() => {
    let timelineChart: echarts.ECharts | undefined;

    if (timelineChartRef.current) {
      timelineChart = echarts.getInstanceByDom(timelineChartRef.current) || echarts.init(timelineChartRef.current);
    }

    if (!timelineChart) return;

    if (cyberGraphData.length === 0) {
      const timeRangeText =
        timeRange === "7"
          ? "7 days"
          : timeRange === "30"
            ? "30 days"
            : "90 days";
      const noDataOption = {
        graphic: [
          {
            type: "text",
            left: "center",
            top: "middle",
            style: {
              // text: `No Data Available for Last ${timeRangeText}`,
              fontSize: 16,
              fill: theme.palette.text.secondary,
              fontWeight: "normal",
            },
          },
          {
            type: "text",
            left: "center",
            top: "middle",
            style: {
              text: `No Data Available for Last ${timeRangeText}`,
              fontSize: 12,
              fill: theme.palette.text.disabled,
              fontWeight: "normal",
              y: 30,
            },
          },
        ],
      } as echarts.EChartsOption;
      timelineChart.setOption(noDataOption);
    }

    // Check if we have data
    if (!cyberGraphData || cyberGraphData.length === 0) {
      // Show "No Data" message with time range context
      const timeRangeText =
        timeRange === "7"
          ? "7 days"
          : timeRange === "30"
            ? "30 days"
            : "90 days";
      const noDataOption = {
        graphic: [
          {
            type: "text",
            left: "center",
            top: "middle",
            style: {
              // text: `No Data Available for Last ${timeRangeText}`,
              fontSize: 16,
              fill: theme.palette.text.secondary,
              fontWeight: "normal",
            },
          },
          {
            type: "text",
            left: "center",
            top: "middle",
            style: {
              text: `No Data Available for Last ${timeRangeText}`,
              fontSize: 12,
              fill: theme.palette.text.disabled,
              fontWeight: "normal",
              y: 30,
            },
          },
        ],
      } as echarts.EChartsOption;
      timelineChart.setOption(noDataOption);
      return () => {
        timelineChart?.dispose();
      };
    }

    // Filter out invalid data points
    const validData = cyberGraphData.filter(
      (data: any) =>
        (data.date_time &&
          data.intelli_score !== undefined &&
          data.intelli_score !== null) ||
        (data.security_rating !== undefined && data.security_rating !== null) ||
        (data.cve_score !== undefined && data.cve_score !== null)
    );

    if (validData.length === 0) {
      // Show "No Data" message with time range context
      const timeRangeText =
        timeRange === "7"
          ? "7 days"
          : timeRange === "30"
            ? "30 days"
            : "90 days";
      const noDataOption = {
        graphic: [
          {
            type: "text",
            left: "center",
            top: "middle",
            style: {
              text: `No Valid Data Available for Last ${timeRangeText}`,
              fontSize: 16,
              fill: theme.palette.text.secondary,
              fontWeight: "normal",
            },
          },
          {
            type: "text",
            left: "center",
            top: "middle",
            style: {
              text: `No Data Available for Last ${timeRangeText}`,
              fontSize: 12,
              fill: theme.palette.text.disabled,
              fontWeight: "normal",
              y: 30,
            },
          },
        ],
      } as echarts.EChartsOption;
      timelineChart.setOption(noDataOption);
      return () => {
        timelineChart?.dispose();
      };
    }

    const dates = validData.map((data: any) => data.date_time || "");
    const intelliScores = validData.map((data: any) => data.intelli_score || 0);
    const postureScores = validData.map(
      (data: any) => data.security_rating || 0
    );
    const cveScores = validData.map((data: any) => data.cve_score || 0);

    const timelineOption: echarts.EChartsOption = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
        },
        formatter: (params: any) => {
          let tooltipText = `${moment(params[0].axisValue).format(
            "YYYY-MM-DD"
          )}<br/>`;
          params.forEach((item: any) => {
            let color = "#000";
            if (item.seriesName === "Intelliscore") color = "#5590FF";
            if (item.seriesName === "Posture Score") color = "#F03C3C";
            if (item.seriesName === "CVE Score") color = "#00A61B";

            tooltipText += `
            <span style="display:inline-block;margin-right:5px;border-radius:50%;width:8px;height:8px;background-color:${color};"></span>
            <span>${item.seriesName}: <b style="color:${color}">${item.data}</b></span><br/>
          `;
          });
          return tooltipText;
        },
      },
      legend: {
        data: isCloud
          ? [
            {
              name: "Posture Score",
              icon: "circle",
              itemStyle: { color: "#F03C3C" },
            },
          ]
          : [
            {
              name: "Intelliscore",
              icon: "circle",
              itemStyle: { color: "#5590FF" },
            },
            {
              name: "Posture Score",
              icon: "circle",
              itemStyle: { color: "#F03C3C" },
            },
            {
              name: "CVE Score",
              icon: "circle",
              itemStyle: { color: "#00A61B" },
            },
          ],
        selected: {
          Intelliscore: true,
          "Posture Score": true,
          "CVE Score": true,
        },
        top: 0,
        icon: "circle",
        itemWidth: 8,
        itemHeight: 8,
        textStyle: {
          fontSize: "12px",
          color: theme.palette.text.primary,
        },
      },
      grid: {
        top: 40,
        right: 20,
        bottom: 30,
        left: 40,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: dates,
        axisLine: {
          lineStyle: {
            color: theme.palette.mode === "dark" ? "#424242" : "#E5E7EB",
          },
        },
        axisLabel: {
          formatter: (value: string) => moment(value).format("MMM 'YY"),
          interval: Math.max(1, Math.floor(dates.length / 7)),
          color: theme.palette.text.secondary,
        },
      },
      yAxis: {
        type: "value",
        splitLine: {
          show: false,
        },
        axisLabel: {
          color: theme.palette.text.secondary,
        },
      },
      series: isCloud ? [
        {
          name: "Posture Score",
          type: "line",
          smooth: true,
          symbol: "none",
          lineStyle: { color: "#F03C3C", width: 2 },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(240, 60, 60, 0.2)" },
                { offset: 1, color: "rgba(240, 60, 60, 0)" },
              ],
            },
          },
          data: postureScores,
        }

      ] : [
        {
          name: "Intelliscore",
          type: "line",
          smooth: true,
          symbol: "none",
          lineStyle: { color: "#5590FF", width: 2 },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(85, 144, 255, 0.2)" },
                { offset: 1, color: "rgba(85, 144, 255, 0)" },
              ],
            },
          },
          data: intelliScores,
        },
        {
          name: "Posture Score",
          type: "line",
          smooth: true,
          symbol: "none",
          lineStyle: { color: "#F03C3C", width: 2 },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(240, 60, 60, 0.2)" },
                { offset: 1, color: "rgba(240, 60, 60, 0)" },
              ],
            },
          },
          data: postureScores,
        },
        {
          name: "CVE Score",
          type: "line",
          smooth: true,
          symbol: "none",
          lineStyle: { color: "#00A61B", width: 2 },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(0, 166, 27, 0.2)" },
                { offset: 1, color: "rgba(0, 166, 27, 0)" },
              ],
            },
          },
          data: cveScores,
        },
      ],
    };

    timelineChart.setOption(timelineOption);
    timelineChart.resize();

    const handleResize = () => {
      timelineChart?.resize();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      timelineChart?.dispose();
    };
  }, [cyberGraphData, timeRange, theme.palette.mode, theme.palette.text.primary, theme.palette.text.secondary]);

  return (
    <Box
      ref={timelineChartRef}
      style={{ width: "100%", height: "300px" }}
    ></Box>
  );
};

export default TimelineChart;
